// TODO: prop types
/* eslint-disable react/prop-types */

import React, { useCallback, useRef, useState } from 'react';

import { format } from 'date-fns';
import saveAs from 'file-saver';
import ReactSelect from 'react-select';
import { toast } from 'react-hot-toast';
import { useLocalStorage } from 'react-use';

import {
  SortingState,
  SelectionState,
  EditingState,
  PagingState,
  IntegratedPaging,
  IntegratedSorting,
  IntegratedSelection,
  IntegratedSummary,
  SummaryState,
  DataTypeProvider,
  SearchState
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditRow,
  TableEditColumn,
  PagingPanel,
  TableColumnReordering,
  TableFixedColumns,
  TableColumnVisibility,
  ColumnChooser,
  Toolbar,
  TableColumnResizing,
  TableSelection,
  ExportPanel,
  TableSummaryRow,
  DragDropProvider,
  SearchPanel
} from '@devexpress/dx-react-grid-material-ui';
import { Getter, Plugin } from '@devexpress/dx-react-core';

import {
  getFormattedBillableStatus,
  getFormattedTableRows,
  isTimesheetFormDataValid,
  upsertTimesheetRow
} from './helpers';
import timesheetService from '../../../services/TimesheetService';

import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { styled } from '@mui/material/styles';
import { TextField, makeStyles } from '@material-ui/core';
import KebabCommandCell from '../../admin/AdminTimesheet/table/customTemplate/KebabCommandCell';
import CustomToolbarRoot from './customTemplate/CustomToolbarRoot';

const PREFIX = 'Demo';
const classes = {
  lookupEditCell: `${PREFIX}-lookupEditCell`,
  dialog: `${PREFIX}-dialog`,
  inputRoot: `${PREFIX}-inputRoot`,
  selectMenu: `${PREFIX}-selectMenu`
};
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${classes.lookupEditCell}`]: {
    padding: theme.spacing(1)
  },
  [`& .${classes.dialog}`]: {
    width: 'calc(100% - 16px)'
  },
  [`& .${classes.inputRoot}`]: {
    width: '100%'
  },
  [`& .${classes.selectMenu}`]: {
    position: 'absolute !important'
  }
}));

const useStyles = makeStyles({
  root: {
    '& tfoot': {
      display: 'none'
    }
  }
});

const AddButton = ({ onExecute }) => (
  <div style={{ textAlign: 'center' }}>
    <Button color="primary" onClick={onExecute} title="Create new row">
      New
    </Button>
  </div>
);

const EditButton = ({ onExecute }) => (
  <IconButton onClick={onExecute} title="Edit row" size="large">
    <EditIcon />
  </IconButton>
);

const DeleteButton = ({ onExecute }) => (
  <IconButton
    onClick={() => {
      // eslint-disable-next-line
      if (confirm('Are you sure you want to delete this row?')) {
        onExecute();
      }
    }}
    title="Delete row"
    size="large"
  >
    <DeleteIcon />
  </IconButton>
);

const CommitButton = ({ onExecute }) => (
  <IconButton onClick={onExecute} title="Save changes" size="large">
    <SaveIcon />
  </IconButton>
);

const CancelButton = ({ onExecute }) => (
  <IconButton color="secondary" onClick={onExecute} title="Cancel changes" size="large">
    <CancelIcon />
  </IconButton>
);

const commandComponents = {
  add: AddButton,
  edit: EditButton,
  delete: DeleteButton,
  commit: CommitButton,
  cancel: CancelButton
};

const Command = ({ id, onExecute }) => {
  const CommandButton = commandComponents[id];
  return <CommandButton onExecute={onExecute} />;
};

const LookupEditCell = ({ availableColumnValues, value, onValueChange, column, row }) => {
  const columnName = column.name;

  const options = availableColumnValues[columnName].map((item) => {
    return {
      value: item.name || item,
      label: item.name || item,
      id: item.value || item
    };
  });

  const dropdownValue = options.find((option) => option.value === value || option.id === value?.value) || null;
  return (
    <>
      <StyledTableCell className={classes.lookupEditCell}>
        <ReactSelect
          className="basic-single"
          classNamePrefix="select"
          value={dropdownValue}
          isSearchable={true}
          name="color"
          options={options}
          // eslint-disable-next-line no-undef
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          onChange={(selectedOption) => {
            if (
              columnName === 'ClassRef' ||
              columnName === 'CustomerRef' ||
              // columnName === 'EmployeeRef' ||
              columnName === 'VendorRef'
            ) {
              return onValueChange({ name: selectedOption.value, value: selectedOption.id });
            }

            onValueChange(selectedOption.value);
          }}
        />
      </StyledTableCell>
    </>
  );
};

// Lightbox styles injected once (no extra CSS file needed)
const lightboxStyle = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'zoom-out'
  },
  img: {
    maxWidth: '90vw',
    maxHeight: '88vh',
    borderRadius: '6px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
    objectFit: 'contain',
    cursor: 'default'
  },
  close: {
    position: 'absolute',
    top: '16px',
    right: '24px',
    fontSize: '32px',
    color: '#fff',
    cursor: 'pointer',
    lineHeight: 1,
    userSelect: 'none'
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '40px',
    color: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    padding: '0 16px',
    opacity: 0.8
  },
  counter: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#fff',
    fontSize: '13px',
    background: 'rgba(0,0,0,0.4)',
    padding: '2px 10px',
    borderRadius: '12px'
  }
};

const ImageCell = ({ urls }) => {
  const [lightboxIndex, setLightboxIndex] = React.useState(null);
  const isOpen = lightboxIndex !== null;

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % urls.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i - 1 + urls.length) % urls.length);
    };
    // eslint-disable-next-line no-undef
    window.addEventListener('keydown', onKey);
    // eslint-disable-next-line no-undef
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, urls.length]);

  return (
    <>
      {/* Thumbnails row */}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {urls.length > 0 ? (
          urls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`attachment-${i + 1}`}
              onClick={() => setLightboxIndex(i)}
              style={{
                width: '30px',
                height: '30px',
                objectFit: 'cover',
                borderRadius: '3px',
                cursor: 'zoom-in',
                border: '1px solid #ddd'
              }}
            />
          ))
        ) : (
          <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>
        )}
      </div>

      {/* Lightbox modal */}
      {isOpen && (
        <div style={lightboxStyle.overlay} onClick={() => setLightboxIndex(null)}>
          {/* Close */}
          <span style={lightboxStyle.close} onClick={() => setLightboxIndex(null)}>
            ✕
          </span>

          {/* Prev arrow */}
          {urls.length > 1 && (
            <span
              style={{ ...lightboxStyle.arrow, left: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((lightboxIndex - 1 + urls.length) % urls.length);
              }}
            >
              ‹
            </span>
          )}

          {/* Main image — stop propagation so clicking the image doesn't close */}
          <img
            src={urls[lightboxIndex]}
            alt={`attachment-${lightboxIndex + 1}`}
            style={lightboxStyle.img}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next arrow */}
          {urls.length > 1 && (
            <span
              style={{ ...lightboxStyle.arrow, right: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((lightboxIndex + 1) % urls.length);
              }}
            >
              ›
            </span>
          )}

          {/* Counter */}
          {urls.length > 1 && (
            <span style={lightboxStyle.counter}>
              {lightboxIndex + 1} / {urls.length}
            </span>
          )}
        </div>
      )}
    </>
  );
};

const Cell = (props) => {
  if (props.column.name === 'Date') {
    return <Table.Cell {...props} value={format(new Date(props.value), 'dd/MM/yyyy')} />;
  }

  if (props.column.name === 'timeSheetImageUrl') {
    const urls = Array.isArray(props.value) ? props.value : [];
    return (
      <Table.Cell {...props}>
        <ImageCell urls={urls} />
      </Table.Cell>
    );
  }

  return <Table.Cell {...props} />;
};

const EditCell = ({ classRefs, customerRefs, employeeRefs, ...props }) => {
  const columnName = props.column.name;

  const availableColumnValues = {
    ClassRef: classRefs,
    CustomerRef: customerRefs,
    EmployeeRef: employeeRefs,
    BillableStatus: getFormattedBillableStatus()
  };

  if (
    columnName === 'CustomerRef' ||
    columnName === 'ClassRef' ||
    // columnName === 'EmployeeRef' ||
    columnName === 'BillableStatus'
  ) {
    return <LookupEditCell {...props} availableColumnValues={availableColumnValues} />;
  }

  if (columnName === 'Date' || columnName === 'StartTime' || columnName === 'FinishTime') {
    return <Calendar {...props} />;
  }

  return <TableEditRow.Cell {...props} />;
};

const Calendar = ({ ...props }) => {
  const { value, onValueChange, column } = props;

  return (
    <td>
      <TextField
        type={column.name === 'Date' ? 'date' : 'time'}
        onChange={(event) => onValueChange(event.target.value)}
        name={column.name}
        value={column.name === 'Date' ? format(new Date(value), 'yyyy-MM-dd') : value}
      />
    </td>
  );
};

const EditCommandCell = ({ errors, disableEditAndDelete, ...props }) => {
  const children = props.children;
  const row = props.row;

  // const disableEditAndDelete = row.status !== 'WithEmployee';

  const renderEditAndDelete = (child) => {
    if (disableEditAndDelete(row)) {
      return <>-</>;
    }

    return child;
  };

  const commands = React.Children.map(children, (child) =>
    child?.props.id === 'commit'
      ? React.cloneElement(child, { disabled: errors[props.tableRow.rowId] })
      : renderEditAndDelete(child)
  );

  return <KebabCommandCell {...props}>{commands}</KebabCommandCell>;
};

class PatchedIntegratedSelection extends React.PureComponent {
  render() {
    const { rowSelectionEnabled, ...restProps } = this.props;

    return (
      <Plugin>
        <Getter
          name="rows"
          computed={({ rows }) => {
            this.rows = rows;
            return rows.filter(rowSelectionEnabled);
          }}
        />
        <IntegratedSelection {...restProps} />
        <Getter name="rows" computed={() => this.rows} />
      </Plugin>
    );
  }
}

class PatchedTableSelection extends React.PureComponent {
  render() {
    const { rowSelectionEnabled, ...restProps } = this.props;

    return (
      <TableSelection
        cellComponent={(props) =>
          rowSelectionEnabled(props.tableRow.row) ? <TableSelection.Cell {...props} /> : <Table.StubCell {...props} />
        }
        {...restProps}
      />
    );
  }
}

const TableHeaderContent = ({ totalEmployeeCount, totalDateCount, totalHours, column, children, ...restProps }) => {
  return (
    <TableHeaderRow.Content column={column} {...restProps}>
      {column.name === 'EmployeeRef' ? <div style={{ marginRight: '5px' }}>({totalEmployeeCount}) </div> : null}
      {column.name === 'Hours' ? <div style={{ marginRight: '5px' }}>({totalHours}) </div> : null}
      {column.name === 'Day' ? <div style={{ marginRight: '5px' }}>({totalDateCount}) </div> : null}
      {children}
    </TableHeaderRow.Content>
  );
};

const DateFormatter = ({ value }) => <span>{format(value, 'dd/MM/yyyy')}</span>;

const DateTypeProvider = (props) => <DataTypeProvider {...props} formatterComponent={DateFormatter} />;

//customize description cell before downloading
const customizeCell = (cell, row, column) => {
  if (column.name === 'Description') {
    cell.value =
      row.Date.toLocaleDateString() + ' ' + row.EmployeeRef.name + ' ' + row.Description + ' ' + row.variationNote;
  }
  if (column.name === 'Date') {
    cell.value = row.Date.toLocaleDateString();
  }
};

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

const getRowId = (row) => row.id;

export default function VerdTimesheetTable({
  rows,
  columns,
  setRows,
  classRefs,
  customerRefs,
  employeeRefs,
  selection,
  rowSelectionEnabled,
  changeSelection,
  disableEditAndDelete,
  hideComponent,
  dateColumns,
  sorting,
  setSorting,
  filter,
  editingStateColumnExtensions,
  setFilter,
  onAddTime,
  timesheetOptions,
  onTimesheetOption,
  isAddTimeDisabled,
  isAddTimeLoading
}) {
  const [columnOrder, setColumnOrder] = useLocalStorage('defaultTimeSheetColumnOrder', [
    'EmployeeRef',
    'KeyPayEmployeeId',
    'StartTime',
    'FinishTime',
    'Hours',
    'Day',
    'Date',
    'Description',
    'status',
    'CustomerRef',
    'ClassRef',
    'BillableStatus',
    'variationNote',
    'workType',
    'timeSheetImageUrl',
    'VendorRef',
    'email',
    'jobTitle'
  ]);
  const [defaultColumnWidths, setDefaultColumnWidths] = useLocalStorage('defaultTimeSheetColumnWidths', [
    { columnName: 'EmployeeRef', width: 180 },
    { columnName: 'KeyPayEmployeeId', width: 180 },
    { columnName: 'StartTime', width: 120 },
    { columnName: 'FinishTime', width: 120 },
    { columnName: 'Hours', width: 120 },
    { columnName: 'Day', width: 120 },
    { columnName: 'Date', width: 180 },
    { columnName: 'Description', width: 180 },
    { columnName: 'status', width: 180 },
    { columnName: 'CustomerRef', width: 180 },
    { columnName: 'ClassRef', width: 180 },
    { columnName: 'BillableStatus', width: 180 },
    { columnName: 'variationNote', width: 180 },
    { columnName: 'workType', width: 180 },
    { columnName: 'timeSheetImageUrl', width: 240 },
    { columnName: 'VendorRef', width: 180 },
    { columnName: 'email', width: 180 },
    { columnName: 'jobTitle', width: 180 }
  ]);

  const [hiddenColumnNames, setHiddenColumnNames] = useLocalStorage('hiddenTimeSheetColumnNames', [
    'timeSheetImageUrl',
    'KeyPayEmployeeId',
    'workType',
    'jobTitle',
    'email',
    'VendorRef'
  ]);

  const [editingRowIds, getEditingRowIds] = useState([]);
  const [rowChanges, setRowChanges] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [pageSizes] = useState([25, 50, 0]);
  const [validationErrors, setValidationErrors] = useState({});

  const [leftFixedColumns] = useState([TableEditColumn.COLUMN_TYPE]);

  const [totalSummaryItems] = useState([
    { columnName: 'Hours', type: 'Hours' },
    { columnName: 'Date', type: 'Date' },
    { columnName: 'KeyPayEmployeeId', type: 'KeyPayEmployeeId' },
    { columnName: 'EmployeeRef', type: 'EmployeeRef' }
  ]);

  const [totalDateCount, setTotalDateCount] = useState(0);
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);
  const [totalHours, setTotalHours] = useState(0);

  const [addedRows, setAddedRows] = useState([]);

  const classes = useStyles();

  //to calculate timesheet date
  const summaryCalculator = (type, rows, getValue) => {
    // eslint-disable-next-line no-undef
    const selectionSet = new Set(selection);
    const selectedRows = rows.filter((row) => selectionSet.has(row.id));
    if (type === 'Date') {
      const changedRows = selectedRows.length
        ? selectedRows.map((row) => new Date(getValue(row)).getTime()).filter(Number)
        : rows.map((row) => new Date(getValue(row)).getTime()).filter(Number);
      // eslint-disable-next-line no-undef
      const duplicateRemovedRows = new Set([...changedRows]);
      let duplicateRemovedRowsLength = duplicateRemovedRows.size;
      setTotalDateCount(duplicateRemovedRowsLength);
      return duplicateRemovedRowsLength;
    }
    if (type === 'KeyPayEmployeeId') {
      const changedRows = selectedRows.length
        ? selectedRows
            .map((row) => {
              if (!getValue(row)) return;
              getValue(row);
            })
            .filter(Number)
        : rows
            .map((row) => {
              if (!getValue(row)) return;
              getValue(row);
            })
            .filter(Number);
      // eslint-disable-next-line no-undef
      const duplicateRemovedRows = new Set([...changedRows]);
      let duplicateRemovedRowsLength = duplicateRemovedRows.size;
      return duplicateRemovedRowsLength;
    }
    if (type === 'Hours') {
      const totalHours = selectedRows.length
        ? selectedRows
            .map((row) => getValue(row))
            .filter(Number)
            .reduce((a, c) => a + c, 0)
        : rows
            .map((row) => getValue(row))
            .filter(Number)
            .reduce((a, c) => a + c, 0);

      setTotalHours(totalHours);
      return totalHours;
    }
    if (type === 'EmployeeRef') {
      const changedRows = selectedRows.length
        ? selectedRows.map((row) => {
            if (!getValue(row)) return;
            return getValue(row);
          })
        : rows.map((row) => {
            if (!getValue(row)) return;
            return getValue(row);
          });

      // eslint-disable-next-line no-undef
      const duplicateRemovedRows = new Set([...changedRows]);
      let duplicateRemovedRowsLength = duplicateRemovedRows.size;
      setTotalEmployeeCount(duplicateRemovedRowsLength);
      return duplicateRemovedRowsLength;
    }
    return IntegratedSummary.defaultCalculator(type, rows, getValue);
  };

  const formatlessSummaryTypes = ['Hours', 'Date', 'KeyPayEmployeeId', 'EmployeeRef'];

  const messages = {
    Hours: 'TotalHours',
    Date: 'TotalDays',
    KeyPayEmployeeId: 'TotalEmployees',
    EmployeeRef: 'TotalEmployees'
  };

  const exporterRef = useRef(null);

  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const deleteRows = (deletedIds) => {
    const rowsForDelete = rows.slice();
    deletedIds.forEach((rowId) => {
      const index = rowsForDelete.findIndex((row) => row.id === rowId);
      if (index > -1) {
        rowsForDelete.splice(index, 1);
      }
    });
    return rowsForDelete;
  };

  const changeAddedRows = (value) => {
    setAddedRows(
      value.map((row) =>
        Object.keys(row).length
          ? row
          : {
              Hours: 8,
              EmployeeRef: 'Asmita G',
              // eslint-disable-next-line no-undef
              StartTime: localStorage.getItem('startTime') || '07:00',
              // eslint-disable-next-line no-undef
              FinishTime: localStorage.getItem('finishTime') || '15:00',
              Date: new Date(),
              CustomerRef: '',
              ClassRef: '',
              status: 'WithEmployee',
              BillableStatus: 'false'
            }
      )
    );
  };

  const commitChanges = ({ added, changed, deleted }) => {
    let changedRows;
    //if new button is clicked
    if (added) {
      const addedRow = added[0];
      // eslint-disable-next-line no-undef
      if (addedRow.StartTime) localStorage.setItem('startTime', addedRow.StartTime);
      // eslint-disable-next-line no-undef
      if (addedRow.FinishTime) localStorage.setItem('finishTime', addedRow.FinishTime);

      const toBoolean = addedRow.BillableStatus === 'false' ? false : true;

      const addedTimesheet = {
        startTime: addedRow.StartTime,
        finishTime: addedRow.FinishTime,
        date: addedRow.Date,
        description: addedRow.Description ? addedRow.Description : '',
        isBillable: toBoolean,
        client: addedRow.CustomerRef,
        task: addedRow.ClassRef,
        variationNote: addedRow.variationNote ? addedRow.variationNote : '',
        employeeRef: addedRow.EmployeeRef
      };

      const timesheetPayload = isTimesheetFormDataValid(addedTimesheet);

      if (!timesheetPayload) {
        return toast.error('Please fill required data');
      }

      timesheetService
        .addTimesheet(timesheetPayload)
        .then(({ data }) => {
          toast.success('Successfully added timesheet.');

          let timesheetData = [];
          timesheetData.push(data.data);
          const formattedTimesheet = getFormattedTableRows(timesheetData);
          setRows((currentRows) => upsertTimesheetRow(currentRows, formattedTimesheet[0]));
        })
        .catch((error) => {
          toast.error('could not add timesheet' || error);
        });
    }

    //if edit button clicked
    if (changed) {
      changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed[row.id] } : row));

      const changedRowId = Object.keys(changed)[0];
      const changedData = changed[changedRowId];
      if (changedData) {
        // eslint-disable-next-line no-undef
        if (changedData.StartTime) localStorage.setItem('startTime', changedData.StartTime);
        // eslint-disable-next-line no-undef
        if (changedData.FinishTime) localStorage.setItem('finishTime', changedData.FinishTime);
      }

      // update edited data
      if (changed[changedRowId]) {
        timesheetService
          .editTimesheet(changedRowId, changed[changedRowId])
          .then(({ data }) => {
            const updatedRows = changedRows.map((row) => {
              if (row.id === data.data._id) {
                row.Date = new Date(data.data.Date);
              }
              return row;
            });
            setRows(updatedRows);

            toast.success('Successfully edited timesheet');
          })
          .catch((error) => {
            toast.error("Couldn't update timesheet. Something's wrong..." || error);
          });
      }
    }

    //if delete button clicked
    if (deleted) {
      // eslint-disable-next-line no-undef
      if (window.confirm('Are you sure you want to delete this row?')) {
        changedRows = deleteRows(deleted);

        timesheetService
          .deleteTimesheet(deleted[0])
          .then(() => {
            toast.success('Successfully deleted selected timesheet');
            setRows(changedRows);
          })
          .catch((error) => {
            toast.error('Could not delete selected timesheet' || error);
          });
      }
    }
  };

  const editCellComponent = useCallback(
    (props) => {
      return <EditCell {...props} classRefs={classRefs} customerRefs={customerRefs} employeeRefs={employeeRefs} />;
    },
    [classRefs, customerRefs, employeeRefs]
  );

  const validate = (rows, columns) =>
    Object.entries(rows).reduce(
      (acc, [rowId, row]) => ({
        ...acc,
        [rowId]: columns.some((column) => column.required && row[column.name] === '')
      }),
      {}
    );

  const onEdited = (edited) => setValidationErrors(validate(edited, columns));

  const onAdded = (added) => setValidationErrors(validate(added, columns));

  const handleSearchValueChange = useCallback(
    (searchQuery) => {
      setFilter((currentFilter) => ({ ...currentFilter, searchQuery }));
      setCurrentPage(0);
    },
    [setFilter, setCurrentPage]
  );

  return (
    <div>
      <div style={{ margin: '10px 0px' }}>
        <div>Total rows selected: {selection ? selection.length : 0}</div>
      </div>
      <Paper className={classes.root}>
        <Grid rows={rows} columns={columns} getRowId={getRowId}>
          <SortingState sorting={sorting} onSortingChange={setSorting} />
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
          <SelectionState selection={selection} onSelectionChange={changeSelection} />
          <EditingState
            editingRowIds={editingRowIds}
            onEditingRowIdsChange={getEditingRowIds}
            rowChanges={rowChanges}
            onRowChangesChange={(changes) => {
              onEdited(changes);
              setRowChanges(changes);
            }}
            addedRows={addedRows}
            onAddedRowsChange={(added) => {
              onAdded(added);
              changeAddedRows(added);
            }}
            onCommitChanges={commitChanges}
            columnExtensions={editingStateColumnExtensions}
          />
          <SummaryState totalItems={totalSummaryItems} />
          <IntegratedSorting />
          <IntegratedPaging />

          <SearchState value={filter.searchQuery} onValueChange={handleSearchValueChange} />

          <PatchedIntegratedSelection rowSelectionEnabled={rowSelectionEnabled} />
          <IntegratedSummary calculator={summaryCalculator} />

          <DragDropProvider />
          <DateTypeProvider for={dateColumns} />

          <Table cellComponent={Cell} />

          <PatchedTableSelection showSelectAll highlightRow rowSelectionEnabled={rowSelectionEnabled} />

          <TableColumnReordering order={columnOrder} onOrderChange={setColumnOrder} />
          <TableColumnResizing
            defaultColumnWidths={defaultColumnWidths}
            onColumnWidthsChange={setDefaultColumnWidths}
          />
          <TableHeaderRow
            contentComponent={(props) => (
              <TableHeaderContent
                {...props}
                totalEmployeeCount={totalEmployeeCount}
                totalDateCount={totalDateCount}
                totalHours={totalHours}
              />
            )}
            showSortingControls
          />
          <TableColumnVisibility
            hiddenColumnNames={hiddenColumnNames}
            onHiddenColumnNamesChange={setHiddenColumnNames}
          />

          <Toolbar
            rootComponent={(toolbarProps) => (
              <CustomToolbarRoot
                {...toolbarProps}
                onAddTime={onAddTime}
                timesheetOptions={timesheetOptions}
                onTimesheetOption={onTimesheetOption}
                isAddTimeDisabled={isAddTimeDisabled}
                isAddTimeLoading={isAddTimeLoading}
              />
            )}
          />

          <SearchPanel />

          <ColumnChooser />

          <TableEditRow cellComponent={editCellComponent} />

          {/* show new button only in admin timesheet */}
          {(hideComponent && filter.timesheetStatus === 'All') ||
          (hideComponent && filter.timesheetStatus === 'WithEmployee') ? (
            <TableEditColumn
              width={60}
              showAddCommand={!addedRows.length}
              showEditCommand
              showDeleteCommand
              cellComponent={(props) => (
                <EditCommandCell {...props} disableEditAndDelete={disableEditAndDelete} errors={validationErrors} />
              )}
            />
          ) : (
            <TableEditColumn
              width={60}
              showEditCommand
              showDeleteCommand
              cellComponent={(props) => (
                <EditCommandCell {...props} disableEditAndDelete={disableEditAndDelete} errors={validationErrors} />
              )}
            />
          )}

          <TableSummaryRow formatlessSummaryTypes={formatlessSummaryTypes} messages={messages} />

          <TableFixedColumns leftColumns={leftFixedColumns} />
          <PagingPanel pageSizes={pageSizes} />
          {hideComponent && <ExportPanel startExport={startExport} />}
        </Grid>

        <div style={{ display: 'flex', flexWrap: 'wrap', padding: '10px' }}>
          <GridExporter
            ref={exporterRef}
            rows={rows}
            hiddenColumnNames={hiddenColumnNames}
            columns={columns}
            selection={selection}
            onSave={onSave}
            getRowId={getRowId}
            customizeCell={customizeCell}
          />
        </div>
      </Paper>
    </div>
  );
}
